import axios from "axios";
import { emailSenderUrl, firebaseConfig } from "../../constants";
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithCustomToken,
  onAuthStateChanged,
} from "firebase/auth";
import { getMessaging, getToken } from "firebase/messaging";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import bcrypt from "bcryptjs";

export const initFireBase = () => {
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  const auth = getAuth(app);
  const messaging = getMessaging(app);
  const firestore = getFirestore(app);
  const storage = getStorage(app);

  return { auth, messaging, firestore, app, storage };
};

export const authenticateFirestore = async (
  hashedVaultId,
  passKeyIdHash,
  dbCollection,
) => {
  try {
    const { auth } = initFireBase();

    const authenticateRes = await axios({
      url: emailSenderUrl,
      method: "POST",
      data: dbCollection
        ? {
            userId: hashedVaultId,
            type: "notify_init",
            password: passKeyIdHash,
            db: dbCollection,
          }
        : {
            userId: hashedVaultId,
            type: "notify_init",
            password: passKeyIdHash,
          },
    });

    if (authenticateRes?.data?.token) {
      const credential = await signInWithCustomToken(
        auth,
        authenticateRes?.data?.token,
      );

      const userIdToken = await credential?.user?.getIdToken();
      return userIdToken;
    } else {
      return null;
    }
  } catch (err) {
    if (process.env.REACT_APP_NODE_ENV !== "production") {
      console.error("authenticate firestore failed with error: ", err?.message);
    }

    return null;
  }
};

export const getAndSaveToken = async (hashedVaultId) => {
  const { auth, messaging, firestore } = initFireBase();

  const saveFcmToken = async (hashedVaultId) => {
    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      );
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAP_ID,
        serviceWorkerRegistration: registration,
      });
      // console.log("✅ FCM Token:", token);
      const currentToken = token;
      if (!currentToken) {
        console.warn("No FCM token available");
        return;
      }

      const userDoc = doc(
        firestore,
        "verifiedWalletExtensionUsers",
        hashedVaultId,
      );
      await setDoc(
        userDoc,
        {
          fcmToken: {
            token: currentToken,
            updatedAt: new Date(),
          },
        },
        { merge: true },
      );
      // console.log("FCM token saved to Firestore....");
      return true;
    } catch (err) {
      console.error("Error saving FCM token:", err);
      return false;
    }
  };

  await saveFcmToken(hashedVaultId);

  onAuthStateChanged(auth, async () => {
    await saveFcmToken(hashedVaultId);
  });
};

export const updatePassword = async (hashedVaultId, passKeyIdHash) => {
  try {
    const { firestore } = initFireBase();
    const userDoc = doc(
      firestore,
      "verifiedWalletExtensionUsers",
      hashedVaultId,
    );

    const docSnap = await getDoc(userDoc);

    if (docSnap.exists()) {
      const passKeyIdRefHash = await bcrypt.hash(passKeyIdHash, 10);
      await setDoc(
        userDoc,
        {
          passKeyRefHash: passKeyIdRefHash,
        },
        { merge: true },
      );
    }

    return true;
  } catch (err) {
    console.error("Error saving new user:", err);
    return false;
  }
};

export const saveNewUser = async (
  hashedVaultId,
  vaultId,
  channel,
  cosignersIv,
  passKeyIdHash,
) => {
  try {
    const { firestore } = initFireBase();
    const userDoc = doc(
      firestore,
      "verifiedWalletExtensionUsers",
      hashedVaultId,
    );
    const passKeyIdRefHash = await bcrypt.hash(passKeyIdHash, 10);
    await setDoc(
      userDoc,
      {
        id: vaultId,
        hashedId: hashedVaultId,
        channel,
        cosignersIv,
        cosigners: [],
        passKeyRefHash: passKeyIdRefHash,
      },
      { merge: true },
    );
    return true;
  } catch (err) {
    console.error("Error saving new user:", err);
    return false;
  }
};

export const updateUserCosigners = async (
  hashedVaultId,
  cosigner,
  isRemoved,
) => {
  try {
    const { firestore } = initFireBase();
    const userDoc = doc(
      firestore,
      "verifiedWalletExtensionUsers",
      hashedVaultId,
    );

    const docSnap = await getDoc(userDoc);

    if (docSnap.exists()) {
      const data = docSnap.data();
      await setDoc(
        userDoc,
        {
          cosigners: !isRemoved
            ? [...data.cosigners, cosigner]
            : data.cosigners?.filter((cng) => cng?.toLowerCase() !== cosigner),
        },
        { merge: true },
      );
    }

    return true;
  } catch (err) {
    console.error("Error saving new user:", err);
    return false;
  }
};

export const getUser = async (hashedVaultId) => {
  try {
    const { firestore } = initFireBase();
    const userDoc = doc(
      firestore,
      "verifiedWalletExtensionUsers",
      hashedVaultId,
    );

    const docSnap = await getDoc(userDoc);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data;
    } else {
      return null;
    }
  } catch (err) {
    console.error("error fetching user:", err);
    return null;
  }
};

export const getUserDoc = async (hashedVaultId) => {
  try {
    const { firestore } = initFireBase();
    const userDoc = doc(
      firestore,
      "verifiedWalletExtensionUsers",
      hashedVaultId,
    );

    return userDoc;
  } catch (err) {
    console.error("error fetching user:", err);
    return null;
  }
};

export const getAndSaveTokenKyc = async (hashedVaultId) => {
  const { auth, messaging, firestore } = initFireBase();

  const saveFcmToken = async (hashedVaultId) => {
    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      );
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAP_ID,
        serviceWorkerRegistration: registration,
      });
      // console.log("✅ FCM Token:", token);
      const currentToken = token;
      if (!currentToken) {
        console.warn("No FCM token available");
        return;
      }

      const userDoc = doc(
        firestore,
        process.env.REACT_APP_KYC_DB,
        hashedVaultId,
      );
      await setDoc(
        userDoc,
        {
          fcmToken: {
            token: currentToken,
            updatedAt: new Date(),
          },
        },
        { merge: true },
      );
      // console.log("FCM token saved to Firestore....");
      return true;
    } catch (err) {
      console.error("Error saving FCM token:", err?.message);
      return false;
    }
  };

  await saveFcmToken(hashedVaultId);

  onAuthStateChanged(auth, async () => {
    await saveFcmToken(hashedVaultId);
  });
};

export const getUserKyc = async (hashedUserAddress, hashedUserId) => {
  try {
    const authenticated = await authenticateFirestore(
      hashedUserAddress,
      hashedUserId,
      process.env.REACT_APP_KYC_DB,
    );
    // console.log("authenticated...: ", authenticated);
    if (authenticated) {
      await getAndSaveTokenKyc(hashedUserAddress);
      const { firestore } = initFireBase();
      const userDoc = doc(
        firestore,
        process.env.REACT_APP_KYC_DB,
        hashedUserAddress,
      );

      const docSnap = await getDoc(userDoc);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return data;
      } else {
        return null;
      }
    } else {
      return null;
    }
  } catch (err) {
    console.error("error fetching user:", err?.message);
    return null;
  }
};
